/**
 * Client-side image resize / encode before CMS upload.
 * Marketing defaults: WebP at quality 0.78, width capped per call site.
 */

export type ReduceImageOptions = {
  /** Max longest edge in CSS pixels. */
  width?: number;
  /**
   * Output MIME. Default `image/webp` for marketing.
   * Use `keep` to preserve the source type.
   */
  format?: "image/webp" | "image/jpeg" | "image/png" | "keep";
  /** Canvas encoder quality 0–1 (WebP/JPEG). Default 0.78. */
  quality?: number;
};

const DEFAULT_QUALITY = 0.78;

export async function toReducedBlob(
  file: File,
  options: ReduceImageOptions = {},
): Promise<File> {
  const { width, format = "image/webp", quality = DEFAULT_QUALITY } = options;

  const outType =
    format === "keep"
      ? normalizeMime(file.type) || "image/jpeg"
      : format;

  // SVG and similar: skip canvas pipeline.
  if (file.type === "image/svg+xml") {
    return file;
  }

  if (!width && format === "keep") {
    return file;
  }

  const img = await loadImage(file);
  const blob = await imageToBlob(img, outType, width, quality);
  const ext = extensionForMime(outType);
  const baseName = file.name.replace(/\.[^.]+$/, "") || "image";
  return new File([blob], `${baseName}.${ext}`, { type: outType });
}

function normalizeMime(type: string): string {
  if (type === "image/jpg") return "image/jpeg";
  return type;
}

function extensionForMime(mime: string): string {
  if (mime === "image/webp") return "webp";
  if (mime === "image/png") return "png";
  return "jpg";
}

async function loadImage(file: File): Promise<HTMLImageElement> {
  if (!file.type.startsWith("image/")) {
    throw new TypeError(`File type must be an image, but got: ${file.type}`);
  }

  const img = new Image();
  const url = URL.createObjectURL(file);

  try {
    await new Promise<void>((resolve, reject) => {
      img.addEventListener("load", () => resolve());
      img.addEventListener("error", () =>
        reject(new Error(`Failed to load image: ${file.name}`)),
      );
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function imageToBlob(
  img: HTMLImageElement,
  fileType: string,
  maxSize?: number,
  quality: number = DEFAULT_QUALITY,
): Promise<Blob> {
  if (!img.complete || !img.naturalWidth) {
    throw new Error("Image has not been properly loaded");
  }

  if (!fileType.startsWith("image/")) {
    throw new Error(`File type must be an image, but got: ${fileType}`);
  }

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to create canvas 2D context");
  }

  const scaleFactor = maxSize
    ? Math.min(1, maxSize / Math.max(img.naturalWidth, img.naturalHeight))
    : 1;
  const width = Math.round(img.naturalWidth * scaleFactor);
  const height = Math.round(img.naturalHeight * scaleFactor);

  canvas.width = width;
  canvas.height = height;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, width, height);

  const supportsQuality =
    fileType === "image/jpeg" ||
    fileType === "image/webp" ||
    fileType === "image/jpg";

  const blob = await new Promise<Blob | null>((resolve) => {
    if (supportsQuality) {
      canvas.toBlob(resolve, fileType === "image/jpg" ? "image/jpeg" : fileType, quality);
    } else {
      canvas.toBlob(resolve, fileType);
    }
  });

  if (!blob) {
    throw new Error("Failed to convert canvas to Blob");
  }

  return blob;
}
